import React from 'react';
import { ExpertService } from '../../../types';
import ServiceCard from './ServiceCard';

interface ServiceListProps {
  services: ExpertService[];
  isLoading: boolean;
  onEdit: (service: ExpertService) => void;
  onDelete: (service: ExpertService) => void;
}

const ServiceList: React.FC<ServiceListProps> = ({
  services,
  isLoading,
  onEdit,
  onDelete,
}) => {
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
            <div className="mb-3">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
            <div className="h-14 bg-gray-100 rounded-lg mb-4" />
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-1" />
                <div className="h-5 bg-gray-200 rounded w-3/4" />
              </div>
              <div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-1" />
                <div className="h-5 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
            <div className="flex gap-2 pt-4 border-t border-gray-100">
              <div className="flex-1 h-9 bg-gray-200 rounded-lg" />
              <div className="flex-1 h-9 bg-gray-200 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (services.length === 0) {
    return (
      <div className="text-center py-16 px-4">
        <div className="relative mx-auto w-24 h-24 mb-6">
          {/* Decorative background circles */}
          <div className="absolute inset-0 bg-purple-100 rounded-full animate-pulse" />
          <div className="absolute inset-2 bg-purple-50 rounded-full" />
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No services yet</h3>
        <p className="text-gray-600 max-w-sm mx-auto mb-6">
          Create your first service to start accepting bookings from clients.
        </p>
        <p className="text-sm text-gray-500">
          Click the <span className="font-medium text-purple-600">"New Service"</span> button above to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {services.map((service) => (
        <ServiceCard
          key={service.id}
          service={service}
          onEdit={() => onEdit(service)}
          onDelete={() => onDelete(service)}
        />
      ))}
    </div>
  );
};

export default ServiceList;
